import { useState } from "react";
import { Monitor, Tablet, Smartphone, ExternalLink, Search, Heart, Users2, TrendingUp } from "lucide-react";
import { buildPortalTheme, PORTAL_NAV_MODULES, portalHost } from "@/lib/tenant-theme";
import { cn } from "@/lib/utils";

/**
 * Realistic live preview of a tenant's in-progress branding across both
 * portals, with a Desktop/Tablet/Mobile device toggle — matches the
 * reviewed reference screens (Talent = marketing hero with a background
 * image/gradient + search + feature cards; Hub = internal dashboard with
 * a welcome banner, stat cards and mini charts). Branding colors, fonts
 * and logo apply via inline styles on THIS component tree only, never
 * document.documentElement (the Tenant admin chrome is platform-owned;
 * see src/index.css). This maps 1:1 onto what Talent/Hub render for real
 * once the branding is saved to Auth config.
 */
const DEVICES = [
  { id: "desktop", label: "Desktop", icon: Monitor, width: "100%" },
  { id: "tablet", label: "Tablet", icon: Tablet, width: "560px" },
  { id: "mobile", label: "Mobile", icon: Smartphone, width: "340px" },
];

function BrowserFrame({ url, children, testId }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-white shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[10px] text-slate-400">
        <div className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-slate-200" />
          <span className="h-2 w-2 rounded-full bg-slate-200" />
          <span className="h-2 w-2 rounded-full bg-slate-200" />
        </div>
        <span className="truncate">{url}</span>
        <ExternalLink className="h-3 w-3" />
      </div>
      {children}
    </div>
  );
}

function Logo({ theme, label, textColor = "#fff" }) {
  return (
    <div className="flex items-center gap-2">
      {theme.logoUrl ? (
        <img src={theme.logoUrl} alt="" className="h-6 w-auto max-w-[90px] object-contain" />
      ) : (
        <div className="flex h-6 w-6 items-center justify-center rounded font-bold text-[11px]" style={{ backgroundColor: `hsl(${theme.colors.primary})`, color: "#fff" }}>
          {theme.shortName[0]}
        </div>
      )}
      <span className="text-sm font-bold" style={{ fontFamily: theme.fonts.heading, color: textColor }}>{label}</span>
    </div>
  );
}

function TalentPreview({ theme, nav, config, device = "desktop" }) {
  const primary = `hsl(${theme.colors.primary})`;
  const accent = `hsl(${theme.colors.accent})`;
  const secondary = `hsl(${theme.colors.secondary})`;
  const secondaryFg = `hsl(${theme.colors.secondaryForeground})`;
  const background = `hsl(${theme.colors.background})`;
  const navModules = (PORTAL_NAV_MODULES.talent || []).filter((m) => (nav ? nav[m.id] !== false : true));
  const name = config?.displayName || `${theme.shortName} Talent`;
  const tagline = config?.tagline || "Build a Meaningful Career";
  const isMobile = device === "mobile";
  const navLinkCount = device === "tablet" ? 2 : 3;
  // Hero uses the tenant's background image if set, else a branded gradient.
  const heroBg = theme.backgroundImageUrl
    ? `linear-gradient(90deg, rgba(0,0,0,0.55), rgba(0,0,0,0.15)), url("${theme.backgroundImageUrl}") center/cover`
    : `linear-gradient(120deg, ${primary}, hsl(${theme.colors.accent}))`;

  return (
    <BrowserFrame url={`https://${portalHost("talent")}`} testId="preview-talent-portal">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5" style={{ backgroundColor: background }}>
        <Logo theme={theme} label={name} textColor="#0f172a" />
        {isMobile ? (
          // Mobile: a real site collapses the nav into a hamburger menu
          // rather than clipping a horizontal row off the right edge.
          <button type="button" aria-label="Menu" className="flex flex-col gap-[3px] rounded p-1" data-testid="talent-preview-mobile-menu">
            <span className="block h-0.5 w-4 rounded" style={{ backgroundColor: primary }} />
            <span className="block h-0.5 w-4 rounded" style={{ backgroundColor: primary }} />
            <span className="block h-0.5 w-4 rounded" style={{ backgroundColor: primary }} />
          </button>
        ) : (
          <div className="flex min-w-0 items-center gap-2 overflow-hidden text-[11px] font-medium text-slate-500">
            {navModules.slice(1, 1 + navLinkCount).map((m) => <span key={m.id} className="whitespace-nowrap">{m.label}</span>)}
            {/* secondary token: a secondary action pill beside the primary CTA */}
            <span className="whitespace-nowrap rounded px-2 py-1" style={{ backgroundColor: secondary, color: secondaryFg }}>Browse</span>
            <span className="whitespace-nowrap rounded px-2 py-1 text-white" style={{ backgroundColor: primary }}>Sign In</span>
          </div>
        )}
      </div>
      <div className="relative px-5 py-6 text-white" style={{ background: heroBg, fontFamily: theme.fonts.sans }}>
        <h3 className={cn("text-lg font-bold leading-tight", isMobile ? "max-w-full" : "max-w-[70%]")} style={{ fontFamily: theme.fonts.heading }}>{tagline}</h3>
        <p className={cn("mt-1 text-[11px] opacity-90", isMobile ? "max-w-full" : "max-w-[65%]")}>Join our team and make a difference every day.</p>
        <div className={cn("mt-3 flex items-center gap-2 rounded-md bg-white/95 p-1.5", isMobile ? "w-full flex-wrap" : "max-w-[90%]")}>
          <Search className="ml-1 h-3.5 w-3.5 text-slate-400" />
          <span className="flex-1 text-[10px] text-slate-400">Search jobs by title, keyword, or location…</span>
          <span className="whitespace-nowrap rounded px-2 py-1 text-[10px] font-semibold text-white" style={{ backgroundColor: primary }}>Search Jobs</span>
        </div>
      </div>
      {/* feature strip paints the page `background` token so it is visible */}
      <div className={cn("gap-2 p-3", isMobile ? "grid grid-cols-1" : "grid grid-cols-3")} style={{ backgroundColor: background }}>
        {[
          { icon: Heart, t: "Life-Changing Work", s: "Impact lives" },
          { icon: Users2, t: "Great People", s: "Supportive teams" },
          { icon: TrendingUp, t: "Growth", s: "Advance your career" },
        ].map(({ icon: Icon, t, s }) => (
          <div key={t} className="flex flex-col items-start gap-1 rounded-md bg-white p-2 shadow-sm">
            <div className="flex h-6 w-6 items-center justify-center rounded-full" style={{ backgroundColor: accent }}>
              <Icon className="h-3 w-3" style={{ color: primary }} />
            </div>
            <p className="text-[10px] font-semibold text-slate-700">{t}</p>
            <p className="text-[9px] text-slate-400">{s}</p>
          </div>
        ))}
      </div>
    </BrowserFrame>
  );
}

function MiniBarChart({ primary, accent }) {
  const bars = [60, 40, 75, 30, 55, 25];
  return (
    <div className="flex h-14 items-end gap-1">
      {bars.map((h, i) => (
        <div key={i} className="flex-1 rounded-t" style={{ height: `${h}%`, backgroundColor: i % 2 === 0 ? primary : accent }} />
      ))}
    </div>
  );
}

function MiniDonut({ primary, accent }) {
  // Conic-gradient donut echoing the reference "Top Talent Sources".
  return (
    <div
      className="h-14 w-14 rounded-full"
      style={{
        background: `conic-gradient(${primary} 0 38%, ${accent} 38% 62%, #cbd5e1 62% 82%, #e2e8f0 82% 100%)`,
        WebkitMask: "radial-gradient(circle 9px at center, transparent 98%, #000 100%)",
        mask: "radial-gradient(circle 9px at center, transparent 98%, #000 100%)",
      }}
    />
  );
}

function HubPreview({ theme, nav, config, device = "desktop" }) {
  const primary = `hsl(${theme.colors.primary})`;
  const accent = `hsl(${theme.colors.accent})`;
  const secondary = `hsl(${theme.colors.secondary})`;
  const background = `hsl(${theme.colors.background})`;
  const navModules = (PORTAL_NAV_MODULES.hub || []).filter((m) => (nav ? nav[m.id] !== false : true));
  const name = config?.displayName || `${theme.shortName} Hub`;
  const isMobile = device === "mobile";
  const navLinkCount = device === "tablet" ? 3 : 4;
  const stats = [
    { label: "Total Candidates", value: "2,482", delta: "↑ 12%" },
    { label: "Active Vendors", value: "24", delta: "↑ 4%" },
    { label: "Open Positions", value: "18", delta: "↓ 10%" },
    { label: "Hires", value: "312", delta: "↑ 18%" },
  ];
  return (
    <BrowserFrame url={`https://${portalHost("hub")}`} testId="preview-hub-portal">
      <div className="flex items-center justify-between gap-2 px-4 py-2.5" style={{ backgroundColor: primary }}>
        <Logo theme={theme} label={name} />
        {isMobile ? (
          <button type="button" aria-label="Menu" className="flex flex-col gap-[3px] rounded p-1" data-testid="hub-preview-mobile-menu">
            <span className="block h-0.5 w-4 rounded bg-white/85" />
            <span className="block h-0.5 w-4 rounded bg-white/85" />
            <span className="block h-0.5 w-4 rounded bg-white/85" />
          </button>
        ) : (
          <div className="flex min-w-0 items-center gap-3 overflow-hidden text-[11px] font-medium text-white/85">
            {navModules.slice(1, 1 + navLinkCount).map((m) => <span key={m.id} className="whitespace-nowrap">{m.label}</span>)}
          </div>
        )}
      </div>
      {/* dashboard body paints the page `background` token */}
      <div className="p-3" style={{ fontFamily: theme.fonts.sans, backgroundColor: background }}>
        <p className="text-xs font-bold text-slate-800" style={{ fontFamily: theme.fonts.heading }}>Welcome back, John</p>
        <p className="text-[9px] text-slate-400">Here's what's happening with your talent ecosystem.</p>
        <div className={cn("mt-2 grid gap-1.5", isMobile ? "grid-cols-2" : "grid-cols-4")}>
          {stats.map((s) => (
            // stat cards use the `secondary` token as their surface
            <div key={s.label} className="rounded border border-black/5 p-1.5" style={{ backgroundColor: secondary }}>
              <p className="text-[8px] text-slate-500">{s.label}</p>
              <p className="text-xs font-bold text-slate-800">{s.value}</p>
              <p className="text-[8px]" style={{ color: primary }}>{s.delta}</p>
            </div>
          ))}
        </div>
        <div className="mt-2 grid grid-cols-[1fr_auto] gap-3">
          <div>
            <p className="mb-1 text-[9px] font-semibold text-slate-600">Hiring Pipeline</p>
            <MiniBarChart primary={primary} accent={accent} />
          </div>
          <div className="flex flex-col items-center">
            <p className="mb-1 text-[9px] font-semibold text-slate-600">Top Sources</p>
            <MiniDonut primary={primary} accent={accent} />
          </div>
        </div>
      </div>
    </BrowserFrame>
  );
}

export default function BrandingPreview({ branding, portals = ["talent", "hub"], featureConfig = null }) {
  // Each portal gets its own theme: the shared brand with any per-portal
  // color overrides layered on. Unset overrides fall back to the brand,
  // so "default" portals match and a tenant can diverge Talent from Hub.
  const talentTheme = buildPortalTheme(branding, featureConfig?.portals?.talent?.colors);
  const hubTheme = buildPortalTheme(branding, featureConfig?.portals?.hub?.colors);
  const [device, setDevice] = useState("desktop");
  const width = DEVICES.find((d) => d.id === device)?.width || "100%";

  return (
    <div data-testid="branding-preview">
      <div className="mb-3 inline-flex rounded-md border border-border bg-muted p-0.5" role="tablist" aria-label="Preview device">
        {DEVICES.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={device === id}
            onClick={() => setDevice(id)}
            data-testid={`preview-device-${id}`}
            className={cn(
              "flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors",
              device === id ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>
      <div className="mx-auto space-y-4 transition-all" style={{ maxWidth: width }}>
        {portals.includes("talent") && (
          <TalentPreview theme={talentTheme} nav={featureConfig?.portals?.talent?.nav} config={featureConfig?.portals?.talent} device={device} />
        )}
        {portals.includes("hub") && (
          <HubPreview theme={hubTheme} nav={featureConfig?.portals?.hub?.nav} config={featureConfig?.portals?.hub} device={device} />
        )}
      </div>
    </div>
  );
}
